import { bibIssues } from '../analysis/bibliography';
import type { BibEntry } from '../analysis/structure';
import { countOf, truncate } from '../util/text';
import { cmToTwips } from '../util/units';
import { defineRule, type FixAction, type Rule, type RuleContext } from './types';
import { quote } from './util';

const collator = new Intl.Collator('ru', { sensitivity: 'base' });

/** Ключ сортировки записи: без номера, кавычек и скобок в начале. */
const sortKey = (text: string): string =>
  text
    .replace(/^[\s[\]\d.)«"„(]+/, '')
    .toLowerCase()
    .replace(/ё/g, 'е');

function looksAlphabetical(entries: BibEntry[]): boolean {
  if (entries.length < 4) return false;
  let ok = 0;
  for (let i = 1; i < entries.length; i++) if (collator.compare(sortKey(entries[i - 1]!.text), sortKey(entries[i]!.text)) <= 0) ok++;
  return ok / (entries.length - 1) > 0.85;
}

/** Номер записи: явный или порядковый, если записи не пронумерованы. */
function entryNumbers(entries: BibEntry[]): Map<number, BibEntry> {
  const map = new Map<number, BibEntry>();
  const numbered = entries.some((e) => e.number !== undefined);
  entries.forEach((e, i) => {
    const n = numbered ? e.number : i + 1;
    if (n !== undefined && !map.has(n)) map.set(n, e);
  });
  return map;
}

const hasBib = (ctx: RuleContext): boolean => !!ctx.s.bibliography && ctx.s.bibliography.entries.length > 0;

export const referenceRules: Rule[] = [
  defineRule({
    id: 'refs.none',
    category: 'references',
    title: 'Ссылки на источники в тексте',
    severity: 'warning',
    gost: 'ГОСТ 7.32-2017, п. 5.10.2, 6.9.1',
    why: 'На источники ссылаются в тексте номером в квадратных скобках: [1], [2, с. 15]. Номер ссылки совпадает с номером записи в списке.',
    check(ctx) {
      if (!hasBib(ctx) || ctx.s.citations.length) return;
      ctx.report({
        severity: ctx.model.footnotes > 0 ? 'info' : undefined,
        paragraph: ctx.s.bibliography!.heading,
        message:
          ctx.model.footnotes > 0
            ? 'Ссылки на источники оформлены сносками, а ГОСТ 7.32 предполагает номера в квадратных скобках [1]. Уточните требование методички'
            : 'В тексте нет ни одной ссылки на источники вида [1] — список источников ни с чем не связан',
      });
    },
  }),

  defineRule({
    id: 'refs.missing',
    category: 'references',
    title: 'Ссылки на несуществующие источники',
    severity: 'error',
    gost: 'ГОСТ 7.32-2017, п. 6.9.1',
    why: 'Номер в квадратных скобках должен соответствовать записи в списке использованных источников.',
    check(ctx) {
      if (!ctx.s.citations.length) return;
      if (!hasBib(ctx)) {
        const first = ctx.s.citations[0]!;
        ctx.report({ paragraph: first.paragraph, message: 'В тексте есть ссылки вида [1], но список использованных источников не найден' });
        return;
      }
      const entries = ctx.s.bibliography!.entries;
      const known = entryNumbers(entries);
      for (const c of ctx.s.citations) {
        const bad = c.numbers.filter((n) => !known.has(n));
        if (!bad.length) continue;
        ctx.report({
          paragraph: c.paragraph,
          marks: [[c.start, c.end]],
          message: `Ссылка ${c.raw} ведёт на ${bad.length > 1 ? `несуществующие источники ${bad.join(', ')}` : `несуществующий источник ${bad[0]}`}: в списке ${countOf(entries.length, ['запись', 'записи', 'записей'])}`,
        });
      }
    },
  }),

  defineRule({
    id: 'refs.uncited',
    category: 'references',
    title: 'Источники без ссылок',
    severity: 'warning',
    gost: 'ГОСТ 7.32-2017, п. 5.10.2, 6.9.1',
    why: 'В список включают документы, использованные при составлении отчёта, со ссылками в квадратных скобках (п. 5.10.2); сами ссылки стандарт рекомендует (п. 6.9.1). Большинство методичек требуют ссылку на каждый источник: запись без ссылки выглядит как добавленная для объёма.',
    check(ctx) {
      if (!hasBib(ctx) || !ctx.s.citations.length) return;
      const cited = new Set(ctx.s.citations.flatMap((c) => c.numbers));
      for (const [n, e] of entryNumbers(ctx.s.bibliography!.entries)) {
        if (cited.has(n)) continue;
        ctx.report({ paragraph: e.paragraph, message: `На источник ${n} нет ссылки в тексте: ${quote(e.text, 50)}` });
      }
    },
  }),

  defineRule({
    id: 'refs.order',
    category: 'references',
    title: 'Порядок источников',
    severity: 'warning',
    gost: 'ГОСТ 7.32-2017, п. 6.16',
    why: 'По ГОСТ 7.32 источники располагают в порядке появления ссылок в тексте. Многие гуманитарные методички требуют алфавитный порядок — это переключается в настройках.',
    check(ctx) {
      if (!hasBib(ctx)) return;
      const entries = ctx.s.bibliography!.entries;
      const order = ctx.profile.references.order;
      if (order === 'appearance') {
        if (!ctx.s.citations.length) return;
        const known = entryNumbers(entries);
        const firstSeen: number[] = [];
        const at = new Map<number, { paragraph: number; start: number; end: number }>();
        for (const c of ctx.s.citations) {
          for (const n of c.numbers) {
            if (!known.has(n) || at.has(n)) continue;
            at.set(n, c);
            firstSeen.push(n);
          }
        }
        const k = firstSeen.findIndex((n, i) => n !== i + 1);
        if (k < 0) return;
        const n = firstSeen[k]!;
        const c = at.get(n)!;
        const alpha = looksAlphabetical(entries);
        ctx.report({
          paragraph: c.paragraph,
          marks: [[c.start, c.end]],
          message:
            `Источник [${n}] упоминается в тексте раньше источника [${k + 1}] — по ГОСТ 7.32 список упорядочивают по первому упоминанию` +
            (alpha ? '. Похоже, список составлен по алфавиту: если так требует методичка, выберите в настройках порядок «по алфавиту»' : ''),
        });
      } else if (order === 'alphabetical') {
        for (let i = 1; i < entries.length; i++) {
          const a = entries[i - 1]!;
          const b = entries[i]!;
          const aLat = /^[a-z]/i.test(sortKey(a.text));
          const bLat = /^[a-z]/i.test(sortKey(b.text));
          const wrong = aLat && !bLat ? true : aLat === bLat && collator.compare(sortKey(a.text), sortKey(b.text)) > 0;
          if (!wrong) continue;
          ctx.report({
            paragraph: b.paragraph,
            message: `Нарушен алфавитный порядок: ${quote(truncate(b.text, 40))} стоит после ${quote(truncate(a.text, 40))}${aLat && !bLat ? ' (источники на русском идут перед иностранными)' : ''}`,
          });
          break;
        }
      }
    },
  }),

  defineRule({
    id: 'refs.numbering',
    category: 'references',
    title: 'Нумерация списка источников',
    severity: 'warning',
    gost: 'ГОСТ 7.32-2017, п. 6.16',
    why: 'Записи нумеруют арабскими цифрами с точкой и печатают с абзацного отступа.',
    check(ctx) {
      if (!hasBib(ctx)) return;
      const entries = ctx.s.bibliography!.entries;
      const numbered = entries.filter((e) => e.number !== undefined);
      if (!numbered.length) {
        ctx.report({ paragraph: ctx.s.bibliography!.heading, message: 'Записи списка источников не пронумерованы' });
        return;
      }
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i]!;
        if (e.number !== i + 1) {
          ctx.report({
            paragraph: e.paragraph,
            message: e.number === undefined ? 'Запись списка без номера' : `Запись под номером ${e.number}, а по порядку это ${i + 1}-я`,
          });
          break;
        }
      }
      const badLabels = entries.filter((e) => e.label && !/^\d{1,3}\.$/.test(e.label.trim()));
      if (badLabels.length) {
        ctx.report({
          severity: 'info',
          paragraph: badLabels[0]!.paragraph,
          message: `Номера записей оформлены как «${badLabels[0]!.label!.trim()}» (${countOf(badLabels.length, ['запись', 'записи', 'записей'])}) — по ГОСТ 7.32 пишут цифру с точкой: «1.»`,
        });
      }
      const want = cmToTwips(ctx.profile.text.firstLineIndentCm);
      const off = entries.filter((e) => {
        const p = ctx.model.paragraphs[e.paragraph]!;
        return Math.abs((p.props.indLeft ?? 0) + (p.props.indFirst ?? 0) - want) > 60;
      });
      if (off.length) {
        const fix: FixAction[] = off
          .filter((e) => !e.auto)
          .map((e) => ({ kind: 'para' as const, paragraph: e.paragraph, set: { indFirst: want, indLeft: 0 } }));
        ctx.report({
          severity: 'info',
          paragraph: off[0]!.paragraph,
          message: `Записи списка не с абзацного отступа (${countOf(off.length, ['запись', 'записи', 'записей'])})`,
          fix: fix.length ? fix : undefined,
          fixLabel: 'С абзацного отступа',
        });
      }
    },
  }),

  defineRule({
    id: 'refs.format',
    category: 'references',
    title: 'Библиографические записи',
    severity: 'warning',
    aggregate: ['запись', 'записи', 'записей'],
    gost: 'ГОСТ Р 7.0.100-2018',
    why: 'Области записи разделяют точкой и тире «. — », перед издательством ставят « : », перед названием журнала — « // ». Указывают год издания, число страниц книги «256 с.» или страницы статьи «С. 15–20». Пример: Иванов, И. И. Базы данных : учебник / И. И. Иванов. — Москва : Наука, 2023. — 256 с.',
    check(ctx) {
      if (!hasBib(ctx)) return;
      for (const e of ctx.s.bibliography!.entries) {
        const p = ctx.model.paragraphs[e.paragraph]!;
        const label = e.auto ? 0 : (/^\s*\[?\d{1,3}(?:[.)\]]\s*|\s+)(?=\S)/.exec(p.text)?.[0].length ?? 0);
        const issues = bibIssues(p.text, label);
        if (!issues.length) continue;
        const fix: FixAction[] = issues
          .filter((i) => i.replace !== undefined)
          .map((i) => ({ kind: 'text', paragraph: p.index, start: i.start, end: i.end, expect: p.text.slice(i.start, i.end), replace: i.replace! }));
        ctx.report({
          severity: issues.some((i) => i.major) ? undefined : 'info',
          paragraph: p.index,
          marks: issues.map((i): [number, number] => (i.end > i.start ? [i.start, i.end] : [Math.max(label, i.start - 1), i.start + 1])),
          message: `Запись не по ГОСТ Р 7.0.100: ${[...new Set(issues.map((i) => i.problem))].join('; ')}`,
          fix: fix.length ? fix : undefined,
          fixLabel: 'Расставить знаки',
        });
      }
    },
  }),

  defineRule({
    id: 'refs.url',
    category: 'references',
    title: 'Электронные ресурсы',
    severity: 'warning',
    gost: 'ГОСТ Р 7.0.100-2018',
    why: 'Для сайтов указывают «URL:» перед адресом и дату обращения: «— URL: https://… (дата обращения: 01.09.2026)».',
    check(ctx) {
      if (!hasBib(ctx)) return;
      for (const e of ctx.s.bibliography!.entries) {
        const p = ctx.model.paragraphs[e.paragraph]!;
        const m = /(https?:\/\/|www\.)\S+/i.exec(p.text);
        if (!m) continue;
        const problems: string[] = [];
        const fix: FixAction[] = [];
        if (!/URL\s*:\s*$/i.test(p.text.slice(0, m.index))) {
          problems.push('перед адресом нет «URL:»');
          fix.push({ kind: 'text', paragraph: p.index, start: m.index, end: m.index, expect: '', replace: 'URL: ' });
        }
        const date = /\(дата обращения(\s*:?)\s*\d{1,2}\.\d{1,2}\.\d{2,4}\s*\)/i.exec(p.text);
        if (!date) problems.push('нет даты обращения «(дата обращения: ДД.ММ.ГГГГ)»');
        else if (!date[1]!.includes(':')) {
          problems.push('после «дата обращения» нужно двоеточие');
          const at = date.index + '(дата обращения'.length;
          fix.push({ kind: 'text', paragraph: p.index, start: at, end: at, expect: '', replace: ':' });
        }
        if (problems.length) {
          ctx.report({ paragraph: p.index, message: `Электронный ресурс: ${problems.join('; ')}`, fix: fix.length ? fix : undefined, fixLabel: 'Дополнить запись' });
        }
      }
    },
  }),
];
